"use client";

import { Viewport } from "@radix-ui/react-toast";

import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useReducer,
} from "react";

import { Toast as ToastRoot } from "./Toast";

import { ToastProviderRadix } from "./ToastProviderRadix";

const ToastContext = createContext<ToastContextState>({
  addToast: () => {},
  deleteToast: () => {},
});

enum ToastAction {
  ADD = "ADD_TOAST",
  DELETE = "DELETE_TOAST",
}

interface ToastReducerAddAction {
  type: ToastAction.ADD;
  payload: Toast;
}

interface ToastReducerDeleteAction {
  type: ToastAction.DELETE;
  payload: { id: string };
}

type ToastReducerAction = ToastReducerAddAction | ToastReducerDeleteAction;

interface ToastReducerState {
  toasts: Toast[];
}

interface ToastContent {
  title?: string;
  description?: string;
}
interface ToastContextState {
  content?: ToastContent;
  addToast: (variant: ToastVariant, options: AddToastArgs) => void;
  deleteToast: (id: string) => void;
}

interface ToastProviderProps {
  children: ReactNode;
}

interface Toast extends AddToastArgs {
  id: string;
  variant?: ToastVariant;
}

export enum ToastVariant {
  SUCCESS = "success",
  WARNING = "warning",
  INFO = "info",
  ERROR = "error",
}

interface AddToastArgs {
  title: string;
  description?: string;
}

export const toastReducer = (
  state: ToastReducerState,
  action: ToastReducerAction
) => {
  switch (action.type) {
    case "ADD_TOAST":
      return {
        ...state,
        toasts: [...state.toasts, action.payload],
      };
    case "DELETE_TOAST":
      return {
        ...state,
        toasts: state.toasts.filter(
          (toast: Toast) => toast.id !== action.payload.id
        ),
      };
    default:
      throw new Error(`Unhandled action type: ${action.type}`);
  }
};

export function ToastProvider({ children }: ToastProviderProps) {
  const [state, dispatch] = useReducer(toastReducer, { toasts: [] });

  const addToast = useCallback(
    (variant: ToastVariant, options: AddToastArgs) => {
      const id = crypto.randomUUID();

      dispatch({ type: ToastAction.ADD, payload: { id, variant, ...options } });
    },
    []
  );

  const deleteToast = useCallback((id: string) => {
    dispatch({ type: ToastAction.DELETE, payload: { id } });
  }, []);

  return (
    <ToastContext.Provider value={{ addToast, deleteToast }}>
      <ToastProviderRadix swipeDirection="right">
        {state.toasts.map((toast) => (
          <ToastRoot
            id={toast.id}
            key={toast.id}
            description={toast.description}
            title={toast.title}
          />
        ))}
        <Viewport className="[--viewport-padding:_25px] fixed bottom-0 right-0 flex flex-col p-[var(--viewport-padding)] gap-[10px] w-[390px] max-w-[100vw] m-0 list-none z-[2147483647] outline-none" />
      </ToastProviderRadix>
      {children}
    </ToastContext.Provider>
  );
}

export function useToastContext() {
  return useContext(ToastContext);
}
